/* eslint-disable no-unused-vars */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  clearSessionToken,
  getCurrentTenantUser,
  getRefreshToken,
  getSessionToken,
  loginTenantUser,
  logoutTenantUser,
  setSessionTokens,
  signupTenantUser,
  subscribeSessionChanges,
  type CindroSession,
} from '../api';
import { clearWorkspaceSelection } from '../utils/workspace-selection';

type SessionContextValue = {
  session: CindroSession | null;
  sessionLoading: boolean;
  signIn: (event: React.FormEvent<HTMLFormElement>, opts: {
    authMode: 'login' | 'signup';
    authEmail: string;
    authPassword: string;
    authDisplayName: string;
    locationSearch: string;
    onRedirect: (path: string) => void;
  }) => Promise<void>;
  signOut: () => void;
  setWorkspaceVersion: React.Dispatch<React.SetStateAction<number>>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

type SessionProviderProps = {
  children: React.ReactNode;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function SessionProvider({ children, onNavigate }: SessionProviderProps) {
  const [session, setSession] = useState<CindroSession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  // workspaceVersion is passed through to WorkspaceContext via context value
  const [workspaceVersion, setWorkspaceVersion] = useState(0);

  // Session recovery on mount
  useEffect(() => {
    const token = getSessionToken();
    if (!token) {
      setSessionLoading(false);
      return;
    }

    let cancelled = false;
    getCurrentTenantUser(token)
      .then((user) => {
        if (cancelled) return;
        setSession({
          accessToken: getSessionToken() || token,
          refreshToken: getRefreshToken(),
          user
        });
        setSessionLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        clearSessionToken();
        setSession(null);
        setSessionLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signOut = useCallback(() => {
    const tokenForLogout = session?.accessToken;
    clearSessionToken();
    clearWorkspaceSelection();
    if (tokenForLogout) {
      void logoutTenantUser(tokenForLogout).catch(() => null);
    }
    setSession(null);
    onNavigate('/login', { replace: true });
  }, [session?.accessToken, onNavigate]);

  // Subscribe to external session changes
  useEffect(() => {
    return subscribeSessionChanges((nextSession) => {
      if (nextSession) {
        setSession(nextSession);
        return;
      }
      signOut();
    });
  }, [signOut]);

  const signIn = useCallback(async (
    event: React.FormEvent<HTMLFormElement>,
    opts: {
      authMode: 'login' | 'signup';
      authEmail: string;
      authPassword: string;
      authDisplayName: string;
      locationSearch: string;
      onRedirect: (path: string) => void;
    }
  ) => {
    event.preventDefault();
    const { authMode, authEmail, authPassword, authDisplayName, locationSearch, onRedirect } = opts;

    const payload =
      authMode === 'signup'
        ? await signupTenantUser({
            email: authEmail.trim(),
            password: authPassword,
            displayName: authDisplayName.trim()
          })
        : await loginTenantUser({
            email: authEmail.trim(),
            password: authPassword
          });

    setSession(payload);
    setSessionTokens(payload);
    setWorkspaceVersion((v) => v + 1);

    const redirectTo = new URLSearchParams(locationSearch).get('redirect');
    if (redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//')) {
      onRedirect(redirectTo);
    }
  }, []);

  const value: SessionContextValue = {
    session,
    sessionLoading,
    signIn,
    signOut,
    setWorkspaceVersion,
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}

function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}

export { SessionProvider, useSession };
export type { SessionContextValue };
